/**
 * "Sharing and permissions" of an own calendar (the default one or an additional one): the Exchange roles Outlook
 * offers (Owner … Free/Busy time, None) or individual rights ("Custom…") for the organisation (Default), anonymous
 * users and each person. Role presets and bit values come from the API; the role menu and the custom checklist are the
 * shared folder-permission components (same as public folders in the admin console). Saved to the folder's
 * permissions, the same data Outlook and phones use.
 */
import { useI18n } from '@platform/i18n';
import {
  Button,
  Alert,
  CustomRightsDialog,
  Dialog,
  DialogContent,
  DialogBody,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  ErrorState,
  MenuSelect,
  PersonAvatar,
  RecipientInput,
  Skeleton,
  checkNames,
  folderRoleFor,
  toast,
  useAsync,
  useErrorText,
  useFolderRoleOptions,
  type Recipient,
} from '@platform/ui';
import { Trash2Icon } from 'lucide-react';
import { useEffect, useId, useState } from 'react';
import { api, type CalendarInfo, type CalendarPermissions, type CalendarPermissionsInput } from '../api';
import { useAuth } from '../auth';
import { useRecipientLabels } from '../compose-labels';
import { useRecipientDirectory } from '../recipient-directory';
import { useCalendar } from './calendar-context';
import { calendarName } from './events';

/** Role of a person just added (Outlook: Reviewer). */
const NEW_MEMBER_ROLE = 'reviewer';

interface Row {
  principal: string;
  name: string;
  rights: number;
}

export interface SharingSource { name: string; load: () => Promise<CalendarPermissions>; save: (input: CalendarPermissionsInput) => Promise<void>; onDirty?: (dirty: boolean) => void; onBusy?: (busy: boolean) => void }
export function SharingDialog({ calendar, onClose, source, onRequestClose = onClose }: { calendar: CalendarInfo | undefined; onClose: () => void; source?: SharingSource; onRequestClose?: () => void }) {
  const { t } = useI18n();
  const id = calendar?.id;
  const loaded = useAsync(() => (id ? source ? source.load() : api.calendarPermissions(id) : Promise.resolve(undefined)), source?.name ?? id ?? 'closed');
  const name = source?.name ?? (calendar ? calendarName(calendar, t) : '');
  return (
    <Dialog open={calendar !== undefined} onOpenChange={(open) => !open && onRequestClose()}>
      <DialogContent framed className="max-w-2xl">
        <DialogHeader>
          <DialogTitle>{t('webmail.calendar.sharing.titleOf', { name })}</DialogTitle>
          <DialogDescription>{t(source ? 'webmail.groupware.sharingHint' : 'webmail.calendar.sharing.description')}</DialogDescription>
        </DialogHeader>
        {loaded.error ? (
          <DialogBody><ErrorState onRetry={loaded.reload} /></DialogBody>
        ) : !loaded.data || !calendar ? (
          <DialogBody><div className="flex flex-col gap-2" aria-busy="true">
            <Skeleton className="h-8 w-full" />
            <Skeleton className="h-8 w-full" />
          </div></DialogBody>
        ) : (
          <SharingForm key={JSON.stringify(loaded.data)} calendar={calendar} initial={loaded.data} onDone={onClose} onCancel={onRequestClose} source={source} />
        )}
      </DialogContent>
    </Dialog>
  );
}

function SharingForm({ calendar, initial, onDone, onCancel, source }: { calendar: CalendarInfo; initial: CalendarPermissions; onDone: () => void; onCancel: () => void; source?: SharingSource | undefined }) {
  const { t } = useI18n();
  const errorText = useErrorText();
  const { session } = useAuth();
  const { changed } = useCalendar();
  const directory = useRecipientDirectory();
  const { recipientLabels } = useRecipientLabels();
  const roleOptions = useFolderRoleOptions(initial.roles);
  const addId = useId();
  const [rows, setRows] = useState<Row[]>(initial.entries.map(({ principal, name, rights }) => ({ principal, name, rights })));
  const [adding, setAdding] = useState<Recipient[]>([]);
  const [custom, setCustom] = useState<Row>();
  const [saving, setSaving] = useState(false);
  const [saveError, setSaveError] = useState(false);
  const dirty = JSON.stringify(rows) !== JSON.stringify(initial.entries.map(({ principal, name, rights }) => ({ principal, name, rights })));
  useEffect(() => { source?.onDirty?.(dirty); return () => source?.onDirty?.(false); }, [source, dirty]);
  useEffect(() => { source?.onBusy?.(saving); return () => source?.onBusy?.(false); }, [source, saving]);
  const newRights = initial.roles.find((preset) => preset.role === NEW_MEMBER_ROLE)?.rights ?? 0;
  const special = (row: Row) => row.principal === 'default' || row.principal === 'anonymous';
  const label = (row: Row) => (row.principal === 'default' ? t('webmail.calendar.sharing.organisation') : row.principal === 'anonymous' ? t('webmail.calendar.sharing.anonymous') : row.name);
  const setRights = (row: Row, rights: number) => setRows((current) => current.map((r) => (r.principal === row.principal ? { ...r, rights } : r)));

  const add = async (next: Recipient[]) => {
    const checked = await checkNames(next, directory);
    const known = new Set(rows.map((r) => r.principal.toLowerCase()));
    const ready = checked.filter((r) => r.status === 'resolved' && r.email.toLowerCase() !== session?.username.toLowerCase() && !known.has(r.email.toLowerCase()));
    if (ready.length > 0) setRows([...rows, ...ready.map((r) => ({ principal: r.email, name: r.name ?? r.email, rights: newRights }))]);
    setAdding(checked.filter((r) => r.status !== 'resolved'));
  };

  const save = async () => {
    setSaving(true);
    setSaveError(false);
    try {
      const input = { entries: rows.map(({ principal, rights }) => ({ principal, rights })) };
      if (source) await source.save(input); else await api.setCalendarPermissions(calendar.id, input);
      toast.success(t('webmail.calendar.sharing.savedToast'));
      changed();
      onDone();
    } catch (error) {
      setSaveError(true);
      errorText.notify(error);
    } finally {
      setSaving(false);
    }
  };

  const roleSelect = (row: Row) => (
    <MenuSelect
      label={t('webmail.calendar.sharing.levelFor', { name: label(row) })}
      value={folderRoleFor(initial.roles, row.rights)}
      options={roleOptions}
      onChange={(role) => {
        if (role === 'custom') return setCustom(row);
        const preset = initial.roles.find((candidate) => candidate.role === role);
        if (preset) setRights(row, preset.rights);
      }}
      className="min-w-0 flex-1 sm:w-64 sm:flex-none"
    />
  );

  return (
    <>
      <DialogBody><fieldset disabled={saving} className="flex flex-col gap-4">
        {saveError && <Alert variant="destructive">{t('webmail.groupware.saveFailed')}</Alert>}
        <div className="flex flex-col gap-1.5">
          <label htmlFor={addId} className="text-sm font-medium">
            {t('webmail.calendar.sharing.addPerson')}
          </label>
          <RecipientInput id={addId} field="share" label={t('webmail.calendar.sharing.addPerson')} value={adding} onChange={(next) => void add(next)} directory={directory} labels={recipientLabels} placeholder={t('webmail.calendar.sharing.addPlaceholder')} />
        </div>
        <ul className="flex flex-col divide-y rounded-md border" aria-label={t('webmail.calendar.sharing.people')}>
          {rows.map((row) =>
            special(row) ? (
              <li key={row.principal} className="flex flex-wrap items-center gap-3 px-3 py-2">
                <span className="flex min-w-0 basis-full flex-col sm:basis-0 sm:flex-1">
                  <span className="text-sm font-medium">{label(row)}</span>
                  <span className="text-xs text-muted-foreground">{t(row.principal === 'default' ? 'webmail.calendar.sharing.organisationHint' : 'webmail.calendar.sharing.anonymousHint')}</span>
                </span>
                {roleSelect(row)}
              </li>
            ) : (
              <li key={row.principal} className="flex flex-wrap items-center gap-3 px-3 py-2">
                <PersonAvatar name={row.name} email={row.principal} />
                <span className="flex min-w-0 flex-1 basis-3/4 flex-col sm:basis-0">
                  <span className="truncate text-sm font-medium">{row.name}</span>
                  <span className="truncate text-xs text-muted-foreground">{row.principal}</span>
                </span>
                {roleSelect(row)}
                <Button variant="ghost" size="icon-sm" aria-label={t('webmail.calendar.sharing.remove', { name: row.name })} onClick={() => setRows(rows.filter((r) => r !== row))}>
                  <Trash2Icon />
                </Button>
              </li>
            ),
          )}
        </ul>
        <p className="text-xs text-muted-foreground">{t('webmail.calendar.sharing.levelsHint')}</p>
      </fieldset></DialogBody>
      <DialogFooter>
        <Button variant="outline" onClick={onCancel} disabled={saving}>
          {t('common.cancel')}
        </Button>
        <Button onClick={() => void save()} disabled={saving}>
          {t('common.save')}
        </Button>
      </DialogFooter>
      {custom && (
        <CustomRightsDialog
          title={t('webmail.calendar.sharing.customTitle', { name: label(custom) })}
          description={t('webmail.calendar.sharing.customDescription')}
          rightBits={initial.rightBits}
          keys={initial.customRights}
          initial={custom.rights}
          saveLabel={t('webmail.calendar.sharing.customApply')}
          onSave={(rights) => {
            setRights(custom, rights);
            setCustom(undefined);
          }}
          onClose={() => setCustom(undefined)}
        />
      )}
    </>
  );
}
